import dayjs from "dayjs";

export const FIELD_TYPES = {
  TEXT: "text",
  NUMBER: "number",
  DATE: "date",
  SELECTION: "selection",
};

export const TEXT_SEARCH_OPERATORS = [
  "eq",
  "neq",
  "contains",
  "not_contains",
  "starts",
  "ends",
];

export const OPERATORS_BY_TYPE = {
  [FIELD_TYPES.TEXT]: [
    { key: "eq", labelKey: "EQ" },
    { key: "neq", labelKey: "NEQ" },
    { key: "contains", labelKey: "CONTAINS" },
    { key: "not_contains", labelKey: "NOT_CONTAINS" },
    { key: "starts", labelKey: "STARTS" },
    { key: "ends", labelKey: "ENDS" },
    { key: "defined", labelKey: "DEFINED" },
    { key: "not_defined", labelKey: "NOT_DEFINED" },
  ],
  [FIELD_TYPES.DATE]: [
    { key: "is", labelKey: "IS_DATE" },
    { key: "before", labelKey: "BEFORE" },
    { key: "after", labelKey: "AFTER" },
    { key: "between", labelKey: "BETWEEN" },
    { key: "defined", labelKey: "DEFINED" },
    { key: "not_defined", labelKey: "NOT_DEFINED" },
  ],
  [FIELD_TYPES.NUMBER]: [
    { key: "eq", labelKey: "EQ" },
    { key: "neq", labelKey: "NEQ" },
    { key: "gt", labelKey: "GT" },
    { key: "lt", labelKey: "LT" },
    { key: "gte", labelKey: "GTE" },
    { key: "lte", labelKey: "LTE" },
    { key: "between", labelKey: "BETWEEN" },
    { key: "defined", labelKey: "DEFINED" },
    { key: "not_defined", labelKey: "NOT_DEFINED" },
  ],
  [FIELD_TYPES.SELECTION]: [
    { key: "is", labelKey: "IS_SELECTION" },
    { key: "is_not", labelKey: "IS_NOT" },
    { key: "defined", labelKey: "DEFINED" },
    { key: "not_defined", labelKey: "NOT_DEFINED" },
  ],
};

export const NO_VALUE_OPERATORS = ["defined", "not_defined"];

export const RANGE_OPERATORS = ["between"];

export const DATE_PRESETS_KEYS = [
  "TODAY",
  "YESTERDAY",
  "LAST_7_DAYS",
  "LAST_30_DAYS",
  "THIS_MONTH",
  "LAST_MONTH",
];

/** Relative range for a preset key, always computed from "now" (for API + UI). */
export function getDatePresetRange(key) {
  const now = dayjs();
  switch (key) {
    case "TODAY": {
      const d = now.startOf("day");
      return [d, d.endOf("day")];
    }
    case "YESTERDAY": {
      const d = now.subtract(1, "day").startOf("day");
      return [d, d.endOf("day")];
    }
    case "LAST_7_DAYS":
      return [now.subtract(7, "day").startOf("day"), now.endOf("day")];
    case "LAST_30_DAYS":
      return [now.subtract(30, "day").startOf("day"), now.endOf("day")];
    case "THIS_MONTH":
      return [now.startOf("month").startOf("day"), now.endOf("day")];
    case "LAST_MONTH": {
      const start = now.subtract(1, "month").startOf("month").startOf("day");
      const end = now.subtract(1, "month").endOf("month").endOf("day");
      return [start, end];
    }
    default:
      return [now.startOf("day"), now.endOf("day")];
  }
}

/** Infer preset key from user selection (preset click or manual pick that matches a range). */
export function inferDatePresetKey(operator, d1, d2) {
  if (!d1 || !d1.isValid?.()) return undefined;
  if (operator === "between" && d2 && d2.isValid?.()) {
    for (const key of DATE_PRESETS_KEYS) {
      const [r0, r1] = getDatePresetRange(key);
      if (d1.isSame(r0, "day") && d2.isSame(r1, "day")) return key;
    }
    return undefined;
  }
  if (operator === "is") {
    for (const key of DATE_PRESETS_KEYS) {
      const [r0] = getDatePresetRange(key);
      if (d1.isSame(r0, "day")) return key;
    }
  }
  return undefined;
}

const formatDateStartForApi = (date) =>
  date.startOf("day").format("YYYY-MM-DD");

const formatDateEndForApi = (date) =>
  date.endOf("day").format("YYYY-MM-DD HH:mm:ss");

/**
 * Values sent to GraphQL: resolve relative presets at request time.
 * Preserved filters without datePresetKey keep fixed value / value2.
 */
export function resolveDateValuesForApi(filter) {
  if (filter?.datePresetKey) {
    const [r0, r1] = getDatePresetRange(filter.datePresetKey);
    if (filter.operator === "between") {
      return {
        value: formatDateStartForApi(r0),
        value2: formatDateEndForApi(r1),
      };
    }
    if (filter.operator === "is") {
      return {
        value: formatDateStartForApi(r0),
        value2: null,
      };
    }
    return { value: filter?.value, value2: filter?.value2 };
  }

  if (filter?.operator === "between" && filter?.value && filter?.value2) {
    const startDate = dayjs(filter.value);
    const endDate = dayjs(filter.value2);
    return {
      value: formatDateStartForApi(startDate),
      value2: formatDateEndForApi(endDate),
    };
  }

  return { value: filter?.value, value2: filter?.value2 };
}

export const getAvailableFields = (fields, context = {}) =>
  Object.values(fields).filter((f) => !f.visibleWhen || f.visibleWhen(context));

export const getFieldConfig = (fieldKey, fields) =>
  Object.values(fields).find((f) => f.key === fieldKey);

export const getOperatorsForField = (fieldConfig) => {
  if (!fieldConfig) return [];
  if (fieldConfig.operators) return fieldConfig.operators;
  return OPERATORS_BY_TYPE[fieldConfig.type] || [];
};
